import{
 Alert,AppBar,Avatar,Box,Button,Divider,Drawer,IconButton,List,ListItemButton,
 ListItemIcon,ListItemText,Stack,Toolbar,Tooltip,Typography,useMediaQuery
}from'@mui/material';
import{alpha,useTheme}from'@mui/material/styles';
import{
 AccountBalanceWalletOutlined,AdminPanelSettingsOutlined,ChevronLeftOutlined,
 ChevronRightOutlined,DashboardOutlined,FolderOutlined,GroupsOutlined,
 InsightsOutlined,LogoutOutlined,MenuOutlined,PeopleOutline,ReceiptLongOutlined,
 SettingsApplicationsOutlined,TimelineOutlined,AutoAwesomeOutlined
}from'@mui/icons-material';
import{NavLink,Outlet,useNavigate}from'react-router-dom';
import{
 can,canModule,clearPlatformReturnSession,expireSessionForRelease,getSession,post,setSession,
 takePlatformReturnSession
}from'./api';
import{GlobalActivity}from'./components';
import RasanaLogo from'./RasanaLogo';
import{rasanaTokens}from'./theme';
import{useEffect,useState}from'react';

const expandedWidth=276,collapsedWidth=76;
const preferenceKey='rasana.sidebar.collapsed';
const RELEASE_CHECK_MS=60_000;

const groups=[
 {label:'Delivery',items:[
  ['Projects','/projects','project.view','projects',FolderOutlined],
  ['Resources','/resources','resource.view','resources',PeopleOutline],
  ['Roadmap','/roadmap','project.view','projects',TimelineOutlined]
 ]},
 {label:'Commercial',items:[
  ['Finance','/finance','finance.view','finance',AccountBalanceWalletOutlined],
  ['Billing','/billing','billing.view','billing',ReceiptLongOutlined]
 ]},
 {label:'Governance',items:[
  ['Attention Centre','/attention','reporting.guidance.read','reporting',AutoAwesomeOutlined],
  ['Executive Dashboard','/executive','reporting.view','reporting',InsightsOutlined],
  ['Internal SteerCo','/steerco','reporting.view','reporting',GroupsOutlined]
 ]},
 {label:'Administration',items:[
  ['Administration','/admin','organisation.manage','core',AdminPanelSettingsOutlined],
  ['Control Centre','/platform','platform.view','core',SettingsApplicationsOutlined]
 ]}
]as const;

function initials(name?:string){
 return(name??'R').split(/\s+/).filter(Boolean).slice(0,2)
  .map(x=>x[0]).join('').toUpperCase();
}

export default function AppShell(){
 const nav=useNavigate();
 const theme=useTheme();
 const mobile=useMediaQuery(theme.breakpoints.down('md'));
 const s=getSession();
 const platformSession=s?.kind==='platform';
 const support=s?.supportAccess;
 const onboarding=s?.onboardingAccess;

 const[collapsed,setCollapsed]=useState(()=>{
  try{return localStorage.getItem(preferenceKey)==='true';}
  catch{return false;}
 });
 const[mobileOpen,setMobileOpen]=useState(false);
 const[returning,setReturning]=useState(false);

 const compact=!mobile&&collapsed;
 const drawerWidth=compact?collapsedWidth:expandedWidth;

 const changeCollapsed=(value:boolean)=>{
  setCollapsed(value);
  try{localStorage.setItem(preferenceKey,String(value));}catch{}
 };

 useEffect(()=>{
  if(!s?.releaseId)return;

  let cancelled=false;

  const checkRelease=async()=>{
   try{
    const response=await fetch(
     `/version.json?t=${Date.now()}`,
     {cache:'no-store'}
    );

    if(!response.ok||cancelled)return;

    const version=await response.json();
    const deployedRelease=String(version?.releaseId??'').trim();

    if(
     deployedRelease&&
     deployedRelease!==s.releaseId
    ){
     expireSessionForRelease();
     window.location.replace('/login');
    }
   }catch{
    // A temporary static-file/network failure must never sign the user out.
   }
  };

  const onFocus=()=>void checkRelease();
  const onVisibility=()=>{
   if(document.visibilityState==='visible'){
    void checkRelease();
   }
  };

  const timer=window.setInterval(
   ()=>void checkRelease(),
   RELEASE_CHECK_MS
  );

  window.addEventListener('focus',onFocus);
  document.addEventListener('visibilitychange',onVisibility);
  void checkRelease();

  return()=>{
   cancelled=true;
   window.clearInterval(timer);
   window.removeEventListener('focus',onFocus);
   document.removeEventListener('visibilitychange',onVisibility);
  };
 },[s?.releaseId]);

 const logout=()=>{
  setSession(null);
  clearPlatformReturnSession();
  nav('/login');
 };

 const returnToPlatform=async()=>{
  if(!support&&!onboarding)return;
  const platform=takePlatformReturnSession();
  if(!platform){logout();return;}
  const organisationId=s?.organisation?.id;
  setReturning(true);
  setSession(platform);
  try{
   if(support){
    await post('/api/v1/platform/support-access/'+support.grantId+'/revoke',{});
   }
  }catch(error){
   console.warn('[Rasana temporary access] Cleanup could not be confirmed.',error);
  }finally{
   setReturning(false);
   nav(
    onboarding&&organisationId
     ?'/platform/organisations/'+organisationId+'/onboarding'
     :'/platform'
   );
  }
 };

 const roleLabel=platformSession
  ?'Platform Administrator'
  :s?.roles?.length?s.roles.join(' · '):'Organisation User';

 const visibleGroups=groups.map(group=>({
  ...group,
  items:group.items.filter(item=>can(item[2])&&(item[3]==='core'||canModule(item[3])))
 })).filter(group=>group.items.length>0);

 const item=(label:string,to:string,Icon:any)=>{
  const node=<ListItemButton
   component={NavLink}
   to={to}
   onClick={mobile?()=>setMobileOpen(false):undefined}
   sx={{
    position:'relative',color:'rgba(255,255,255,.78)',minHeight:44,
    mx:compact?.75:1.25,mb:.25,px:compact?1.5:1.5,borderRadius:1,
    justifyContent:compact?'center':'flex-start',
    transition:'background-color 120ms ease,color 120ms ease',
    '&:hover':{bgcolor:rasanaTokens.navHover,color:'#FFFFFF'},
    '&.active':{
     bgcolor:rasanaTokens.navActive,color:'#FFFFFF',
     '&::before':{content:'""',position:'absolute',left:0,top:8,bottom:8,width:3,borderRadius:'0 4px 4px 0',bgcolor:rasanaTokens.gold}
    }
   }}
  >
   <ListItemIcon sx={{color:'inherit',minWidth:compact?0:40,justifyContent:'center'}}>
    <Icon fontSize="small"/>
   </ListItemIcon>
   {!compact&&<ListItemText primary={label} primaryTypographyProps={{fontSize:13.5,fontWeight:500}}/>}
  </ListItemButton>;
  return compact?<Tooltip key={to} title={label} placement="right">{node}</Tooltip>:<Box key={to}>{node}</Box>;
 };

 const content=<Box sx={{
  display:'flex',
  flexDirection:'column',
  height:'100%',
  bgcolor:rasanaTokens.navy,
  position:'relative',
  overflow:'visible'
 }}>
  <Toolbar sx={{px:compact?1:2,minHeight:84,justifyContent:compact?'center':'flex-start'}}>
   {compact
    ?<RasanaLogo variant="mark" markSize={40}/>
    :<RasanaLogo variant="horizontal" markSize={42} inverse tagline={false}/>
   }
  </Toolbar>

  {!mobile&&<Tooltip
   title={compact?'Expand navigation':'Collapse navigation'}
   placement="right"
  >
   <IconButton
    size="small"
    onClick={()=>changeCollapsed(!compact)}
    aria-label={compact?'Expand navigation':'Collapse navigation'}
    sx={{
     position:'absolute',
     top:2,
     right:2,
     zIndex:20,
     width:24,
     height:24,
     borderRadius:'15px',
     color:'rgba(255,255,255,.82)',
     bgcolor:alpha(rasanaTokens.navy,.98),
     border:'1px solid rgba(255,255,255,.16)',
     boxShadow:'0 3px 10px rgba(0,0,0,.20)',
     '& .MuiSvgIcon-root':{
      position:'relative',
      top:5
     },
     '&:hover':{
      color:'#FFFFFF',
      bgcolor:rasanaTokens.primary
     }
    }}
   >
    {compact
     ?<ChevronRightOutlined fontSize="small"/>
     :<ChevronLeftOutlined fontSize="small"/>
    }
   </IconButton>
  </Tooltip>}

  {!compact&&<Box sx={{px:2.5,pb:2}}><Typography variant="caption" sx={{color:'rgba(255,255,255,.46)',letterSpacing:'.14em',textTransform:'uppercase'}}>
   {platformSession?'Platform Management':'Management Operating System'}
  </Typography></Box>}

  <Divider sx={{borderColor:'rgba(255,255,255,.09)'}}/>
  <Box sx={{flex:1,overflowY:'auto',overflowX:'hidden',py:1.25}}>
   {!platformSession&&<List disablePadding sx={{mb:.5}}>{item('Overview','/',DashboardOutlined)}</List>}
   {visibleGroups.map(group=><Box key={group.label} sx={{mt:compact?.5:1}}>
    {!compact&&<Typography variant="overline" sx={{px:2.75,py:.75,display:'block',color:'rgba(255,255,255,.38)'}}>
     {platformSession&&group.label==='Administration'?'Platform':group.label}
    </Typography>}
    <List dense disablePadding>{group.items.map(x=>item(x[0],x[1],x[4]))}</List>
   </Box>)}
  </Box>

  <Box sx={{mt:'auto',p:compact?1:2,bgcolor:'rgba(0,0,0,.08)'}}>
   <Divider sx={{mb:2,borderColor:'rgba(255,255,255,.09)'}}/>
   {compact
    ?<Tooltip placement="right" title={<Box><Typography variant="subtitle2">{s?.user?.name}</Typography><Typography variant="caption">{roleLabel}<br/>{s?.dataScope} scope<br/>{s?.organisation?.name}</Typography></Box>}>
      <Avatar sx={{width:38,height:38,mx:'auto',fontSize:13,fontWeight:700,bgcolor:rasanaTokens.primary,color:'#FFFFFF'}}>{initials(s?.user?.name)}</Avatar>
     </Tooltip>
    :<Stack direction="row" spacing={1.25} alignItems="center">
      <Avatar sx={{width:38,height:38,fontSize:13,fontWeight:700,bgcolor:rasanaTokens.primary,color:'#FFFFFF'}}>{initials(s?.user?.name)}</Avatar>
      <Box sx={{minWidth:0}}>
       <Typography variant="body2" fontWeight={600} noWrap sx={{color:'#FFFFFF'}}>{s?.user?.name}</Typography>
       <Typography variant="caption" noWrap sx={{color:'rgba(255,255,255,.66)',display:'block'}}>{roleLabel}</Typography>
       <Typography variant="caption" noWrap sx={{color:'rgba(255,255,255,.42)',display:'block'}}>{platformSession?'Rasana Platform':`${s?.dataScope} scope · ${s?.organisation?.name}`}</Typography>
      </Box>
     </Stack>}
   {compact
    ?<Tooltip title="Sign out" placement="right"><IconButton onClick={logout} sx={{mt:1,color:'rgba(255,255,255,.68)',display:'flex',mx:'auto','&:hover':{color:'#FFFFFF',bgcolor:rasanaTokens.navHover}}}><LogoutOutlined fontSize="small"/></IconButton></Tooltip>
    :<Button fullWidth variant="outlined" startIcon={<LogoutOutlined/>} onClick={logout} sx={{mt:1.5,color:'rgba(255,255,255,.82)',borderColor:'rgba(255,255,255,.18)','&:hover':{color:'#FFFFFF',borderColor:'rgba(255,255,255,.34)',bgcolor:rasanaTokens.navHover}}}>Sign out</Button>}
  </Box>
 </Box>;

 return <Box sx={{display:'flex',minHeight:'100vh',bgcolor:'background.default'}}>
  <GlobalActivity/>
  {mobile&&<AppBar position="fixed" elevation={0} sx={{bgcolor:rasanaTokens.navy,borderBottom:'1px solid rgba(255,255,255,.08)'}}>
   <Toolbar>
    <IconButton edge="start" color="inherit" onClick={()=>setMobileOpen(true)} aria-label="Open navigation"><MenuOutlined/></IconButton>
    <Box sx={{ml:1}}><RasanaLogo variant="horizontal" markSize={32} inverse tagline={false}/></Box>
    <Typography variant="caption" sx={{ml:'auto',color:'rgba(255,255,255,.68)'}}>{s?.user?.name}</Typography>
   </Toolbar>
  </AppBar>}
  <Drawer
   variant={mobile?'temporary':'permanent'} open={mobile?mobileOpen:true} onClose={()=>setMobileOpen(false)} ModalProps={{keepMounted:true}}
   sx={{width:mobile?expandedWidth:drawerWidth,flexShrink:0,'& .MuiDrawer-paper':{width:mobile?expandedWidth:drawerWidth,boxSizing:'border-box',bgcolor:rasanaTokens.navy,color:'white',overflowX:mobile?'hidden':'visible',borderRight:0,transition:theme.transitions.create('width',{duration:theme.transitions.duration.shorter})}}}
  >{content}</Drawer>
  <Box component="main" sx={{flexGrow:1,minWidth:0,bgcolor:rasanaTokens.ivory,px:{xs:2,md:3.5,xl:4},pb:{xs:3,md:4},pt:{xs:10,md:3.5}}}>
   <Box sx={{width:'100%',maxWidth:1600,mx:'auto'}}>
    {onboarding&&<Alert severity="info" sx={{mb:2.5}} action={<Button color="inherit" size="small" disabled={returning} onClick={()=>void returnToPlatform()}>{returning?'Returning…':'Return to Onboarding'}</Button>}>
     Temporary Platform Onboarding access to <strong>{s?.organisation?.name}</strong> · phase {onboarding.phase} · expires {new Date(onboarding.expiresAt).toLocaleString()}.
    </Alert>}
    {support&&<Alert severity="warning" sx={{mb:2.5}} action={<Button color="inherit" size="small" disabled={returning} onClick={()=>void returnToPlatform()}>{returning?'Returning…':'Return to Platform'}</Button>}>
     Legacy support session · expires {new Date(support.expiresAt).toLocaleString()}.
    </Alert>}
    {s?.subscription?.readOnly&&<Alert severity="warning" sx={{mb:2.5}}>
     Your Free Trial is in its read-only grace period. You can review Rasana, but changes are disabled until the Organisation moves to a paid subscription.
    </Alert>}
    <Outlet/>
   </Box>
  </Box>
 </Box>;
}
